import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { rawExportFileName } from './exportFileName'

interface RawTabProps {
  pageText: string | null
  /**
   * The document's display name (UI-41). Optional so existing callers keep
   * compiling; when absent the export falls back to "document", matching the
   * sibling download in DocumentPreviewV2 rather than reverting to the UUID.
   */
  fileName?: string | null
}

export function RawTab({ pageText, fileName }: RawTabProps) {
  const { t } = useTranslation()
  // Annotators are read-only — no raw-data export.
  const isAnnotator = isAnnotatorRole(useAppStore((s) => s.user?.role))

  const handleExportRawData = () => {
    if (!pageText) {
      toast.error(t('common:document_viewer.toasts.no_content_to_export'))
      return
    }

    try {
      // Create a blob with the raw text
      const blob = new Blob([pageText], { type: 'text/plain;charset=utf-8' })

      // Create a download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = rawExportFileName(fileName)

      // Trigger download
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success(t('common:document_viewer.toasts.raw_data_exported'))
    } catch (error) {
      console.error('Failed to export raw data:', error)
      toast.error(t('common:document_viewer.toasts.failed_to_export'))
    }
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-card">
      {/* Header */}
      <div className="px-6 py-3 border-b dark:border-dark-border bg-gray-50 dark:bg-dark-muted">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text">{t('document_viewer:raw.title', { defaultValue: 'Raw Extracted Text' })}</h3>
        <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
          {t('document_viewer:raw.description', { defaultValue: 'Plain text extracted from the document without formatting' })}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <pre className="font-mono text-sm whitespace-pre-wrap break-words text-gray-800 dark:text-dark-text">
          {pageText || t('document_viewer:raw.noText', { defaultValue: 'No text content available' })}
        </pre>
      </div>

      {/* Footer with Export Button */}
      {pageText && !isAnnotator && (
        <div className="border-t dark:border-dark-border p-4 bg-gray-50 dark:bg-dark-muted">
          <Button
            size="sm"
            onClick={handleExportRawData}
            className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Download className="w-4 h-4" />
            {t('document_viewer:raw.export', { defaultValue: 'Export Raw Data' })}
          </Button>
        </div>
      )}
    </div>
  )
}
